<template>
  <div class="action_dks_wrapper">
    <!-- DKS 配置主看板 -->
    <div class="dks_config_board">
      <!-- 顶部行程阈值区 -->
      <div class="dks_layout_header">
        <div v-for="(_, index) in 4" :key="index" class="phase_item">
          <div class="phase_visual">
            <div class="phase_icon_box" :class="getStatusClass(index)">
              <img :src="getPhaseIcon(index)" class="phase_icon" />
            </div>
            <!-- <div class="phase_label">PHASE {{ index + 1 }}</div> -->
          </div>
          <div class="phase_input_row">
            <ActionInput v-model="travels[index]" type="number" step="0.1" @blur="checkTravel(index)" :disabled="disableBottomTravel && (index == 1 || index == 2)" />
            <div class="phase_indicator" :class="[getStatusClass(index), { disabled: disableBottomTravel && (index == 1 || index == 2) }]"></div>
          </div>
        </div>
      </div>

      <div class="dks_layout_content">
        <!-- 核心矩阵区 -->
        <div class="matrix_section">
          <div class="matrix_container">
            <dksPoints :data="dksDatas" :selected-row="selectedRow" :restrict-hold-chain="isJingtaiDKSRestrict || isMG63DKSRestrict" :normalize-hold="isMG63DKSRestrict" @changeKeyFunction="changeKeyFunction" />
          </div>
        </div>

        <!-- 右侧按键映射区 (触发值) -->
        <div class="selectors_section">
          <div v-for="(item, rowIndex) in dksDatas" :key="rowIndex" class="selector_card_item">
            <trigger_btn :focus="selectedRow == rowIndex" :selected="(item.KeyCode?.filter((e) => e > 0).length || 0) > 0 && selectedRow != rowIndex" :vks="item.KeyCode || [0, 0, 0]" :tab="tab" :show-edit="false" @trigger-click="openKeyDialog(rowIndex)" />
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="selectedRow" :base-tab="tab" :selected-keys="dksDatas[selectedRow]?.KeyCode || [0, 0, 0]" @confirm="onKeyConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { BaseTabs, actionSet } from '@/service/driver/action_set'
import { SetType } from '@/service/driver/action_types'
import { onMounted, ref, toRefs, watch, watchEffect } from 'vue'
import trigger_btn from './trigger_btn.vue'
import ActionInput from './ActionInput.vue'
import action_key_dialog from './action_key_dialog.vue'
import { VK } from '@/define/define'
import dksPoints from './dks_points.vue'
import { normalizeKeyCode } from '@/tool/utility'
import { MagnetSeniorType } from '@libs/types/enum'
import { SetMagnetKey } from '@libs/types/response'
import { deviceDKSTravel, deviceDKSTravelLimit } from '@/define/dks'
import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'

const isJingtaiDKSRestrict = specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1) || specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2)
const isMG63DKSRestrict = specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)
import { DeviceManager } from '@/service/device'
import { operate } from '@/service/driver/operate'
import firmware from '@/upgrade/firmware'
import dks_press_icon from '@/assets/img/dks_press_icon.svg'
import dks_up_icon from '@/assets/img/dks_up_icon.svg'
import { delay } from '@libs/utils/tools'

const defaultTravel = deviceDKSTravel(DeviceManager.instance.state.currentPid)
let travels = ref([defaultTravel[0], defaultTravel[1], defaultTravel[2], defaultTravel[3]])

const getPhaseIcon = (index: number) => {
  const icons = [dks_up_icon, dks_press_icon, dks_up_icon, dks_press_icon]
  return icons[index]
}

const showKeyDialog = ref(false)

const DKSDefaultKeys = [
  {
    KeyCode: [0x00],
    KeyFunction: [0x00, 0x00, 0x00, 0x00],
    Travel: defaultTravel[0],
  },
  {
    KeyCode: [0x00],
    KeyFunction: [0x00, 0x00, 0x00, 0x00],
    Travel: defaultTravel[1],
  },
  {
    KeyCode: [0x00],
    KeyFunction: [0x00, 0x00, 0x00, 0x00],
    Travel: defaultTravel[2],
  },
  {
    KeyCode: [0x00],
    KeyFunction: [0x00, 0x00, 0x00, 0x00],
    Travel: defaultTravel[3],
  },
]

let selectedRow = ref(0)
let dksDatas = ref<SetMagnetKey[]>(JSON.parse(JSON.stringify(DKSDefaultKeys)))
let { KeyType, Keys } = toRefs(actionSet.magnetValue)

const props = defineProps<{ tab: BaseTabs; keys: VK[] }>()

const initData = () => {
  const defaultItems = JSON.parse(JSON.stringify(DKSDefaultKeys))

  if (KeyType.value == MagnetSeniorType.SeniorKeyTypeDKS && Array.isArray(Keys.value) && Keys.value.length > 0) {
    const loadedData = JSON.parse(JSON.stringify(Keys.value))
    dksDatas.value = defaultItems.map((def: any, idx: number) => {
      return loadedData[idx] || def
    })
  } else {
    KeyType.value = MagnetSeniorType.SeniorKeyTypeDKS
    dksDatas.value = defaultItems
  }

  // 同步行程滑块数据
  dksDatas.value.forEach((item, index) => {
    if (travels.value[index] !== undefined) {
      travels.value[index] = item.Travel
    }
  })

  // 同步数据回全局，确保 Keys 长度至少为 4
  if (JSON.stringify(dksDatas.value) !== JSON.stringify(Keys.value)) {
    Keys.value = JSON.parse(JSON.stringify(dksDatas.value))
  }
}

const openKeyDialog = (value: number) => {
  selectedRow.value = value
  actionSet.initBaseKeysBySwitchInput(dksDatas.value[selectedRow.value]?.KeyCode || [0, 0, 0])
  showKeyDialog.value = true
}

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  dksDatas.value[selectedRow.value].KeyCode = keys
  showKeyDialog.value = false
}

const getStatusClass = (row: number) => {
  const classes = ['press_down_first', 'press_down_second', 'lift_up_first', 'lift_up_second']
  return classes[row]
}

onMounted(() => {
  initData()
  watchData()
  initDisableBottomTravel()
})

watch(
  () => operate.currentKey.value,
  async () => {
    await delay(100) // 延迟更新数据，确保数据更新完成
    // 防御:延迟期间如果用户已切换到非 DKS 模式(如 END/MT/TGL),
    // 不能再写回 DKS 默认数据,否则会覆盖目标模式的 Keys 导致映射动作丢失
    if (actionSet.setType.value !== SetType.dks) return
    initData()
  },
)

const checkTravel = (index: number) => {
  const limit = deviceDKSTravelLimit(DeviceManager.instance.state.currentPid)
  const min = limit[0]
  const max = limit[1]

  if (travels.value[index] > max) {
    travels.value[index] = max
  }
  if (travels.value[index] < min) {
    travels.value[index] = min
  }
  if (specialCheck(speciaBehiver.SyncDKSFirstAndEndTravel, DeviceManager.instance.state.currentPid) || specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
    if (index == 0 || index == 3) {
      dksDatas.value[0].Travel = travels.value[index]
      dksDatas.value[3].Travel = travels.value[index]
    } else {
      dksDatas.value[1].Travel = travels.value[index]
      dksDatas.value[2].Travel = travels.value[index]
    }
    for (let i = 0; i < travels.value.length; i++) {
      travels.value[i] = dksDatas.value[i].Travel
    }
  } else {
    dksDatas.value[index].Travel = travels.value[index]
    travels.value[index] = dksDatas.value[index].Travel
  }
}

const watchData = () => {
  watch(
    dksDatas,
    (val) => {
      if (JSON.stringify(val) !== JSON.stringify(Keys.value)) {
        Keys.value = JSON.parse(JSON.stringify(val))
      }
    },
    { deep: true },
  )
}

watch(
  () => actionSet.magnetValue.KeyType,
  (value) => {
    if (value == MagnetSeniorType.SeniorKeyTypeNone) {
      dksDatas.value = JSON.parse(JSON.stringify(DKSDefaultKeys))
      travels.value = [defaultTravel[0], defaultTravel[1], defaultTravel[2], defaultTravel[3]]
    }
  },
)

const changeKeyFunction = (row: number, col: number, point: number) => {
  dksDatas.value[row].KeyFunction[col] = point
}

const disableBottomTravel = ref(false)
const initDisableBottomTravel = async () => {
  if (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
    disableBottomTravel.value = true
    return
  }
  const res = specialCheck(speciaBehiver.NotEditDKSBottomTravel, DeviceManager.instance.state.currentPid)
  const fwVersionIs104 = await firmware.fwVersionGreaterThanOrEqual('1.0.4', firmware.fwVersionDvs1)
  const fwVersionIs100 = await firmware.fwVersionGreaterThanOrEqual('1.0.0', firmware.fwVersionDvs2)
  if (res && (fwVersionIs104 || fwVersionIs100)) {
    disableBottomTravel.value = false
    return
  }
  disableBottomTravel.value = res
}
</script>

<style lang="less" scoped>
.action_dks_wrapper {
  display: flex;
  flex-direction: column;
  // width: 100%;
  max-width: 580px; /* Slightly wider as requested (from 520px) */
  margin: 0 auto;

  .dks_config_board {
    background: var(--color-white);
    border-radius: var(--radius-l);
    padding: 16px 32px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
    position: relative;
    /* overflow: hidden; */ /* 注释掉以允许 Tooltip 气泡溢出显示 */
    // border-top: 3px solid var(--brand-color-pure);
    // &::before {
    //   content: '';
    //   position: absolute;
    //   top: 0;
    //   left: 0;
    //   right: 0;
    //   height: 4px;
    //   background: var(--brand-color);
    //   opacity: 0.8;
    //   margin: 0px 12px;
    //   border-radius: var(--radius-l) var(--radius-l) 0 0; /* 手动同步父容器圆角，防止超出 */
    // }

    .dks_layout_header {
      display: flex;
      justify-content: flex-start;
      gap: 26px; /* (86 - 60) */
      margin-bottom: 12px;
      padding-left: 11px; /* To align centers with matrix columns */

      .phase_item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        width: 60px;

        .phase_visual {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;

          .phase_icon_box {
            width: 38px;
            height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--color-gray-1);
            border-radius: var(--radius-m);
            transition: var(--transition-base);
            border: 1px solid rgba(0, 0, 0, 0.03);

            .phase_icon {
              width: 18px;
              height: 18px;
              opacity: 0.8;
            }

            &.press_down_first {
              background: rgba(var(--color-brand-2-rgb), 0.1);
              color: var(--brand-color-pure);
            }
            &.press_down_second {
              background: rgba(0, 188, 125, 0.1);
              color: #00bc7d;
            }
            &.lift_up_first {
              background: rgba(43, 127, 255, 0.1);
              color: #2b7fff;
              .phase_icon {
                transform: rotate(180deg);
              }
            }
            &.lift_up_second {
              background: rgba(169, 92, 247, 0.1);
              color: #a95cf7;
              .phase_icon {
                transform: rotate(180deg);
              }
            }
          }

          .phase_label {
            font-size: 10px;
            font-weight: 800;
            color: var(--color-text-hint);
            letter-spacing: 0.5px;
            text-transform: uppercase;
          }
        }

        .phase_input_row {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          width: 100%;

          :deep(.action_input_wrapper) {
            width: 100%;
            height: 30px;
            border-radius: var(--radius-m);
            background: var(--color-gray-1);
            border: 1px solid transparent;

            input {
              font-size: 13px;
              font-weight: 700;
              color: var(--color-dark);
            }

            &.focus {
              border-color: var(--brand-color-pure);
              background: #fff;
            }
          }

          .phase_indicator {
            width: 40px;
            height: 3px;
            border-radius: 2px;
            background: var(--color-gray-2);
            opacity: 0.1;
            transition: var(--transition-base);

            &.press_down_first {
              background: var(--brand-color-pure);
              opacity: 0.8;
            }
            &.press_down_second {
              background: #00bc7d;
              opacity: 0.8;
            }
            &.lift_up_first {
              background: #2b7fff;
              opacity: 0.8;
            }
            &.lift_up_second {
              background: #a95cf7;
              opacity: 0.8;
            }
            &.disabled {
              background: var(--color-gray-2);
              opacity: 0.05;
            }
          }
        }
      }
    }

    .dks_layout_content {
      display: flex;
      align-items: flex-start;
      gap: 24px; /* Slightly more breathing room (from 16px) */
      margin-top: 10px;

      .matrix_section {
        flex: 0 0 340px;
        background: var(--color-gray-1);
        border-radius: var(--radius-m);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(0, 0, 0, 0.04);
        padding: 4px 0;
        box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.02);
      }

      .selectors_section {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding-top: 10px;

        .selector_card_item {
          // width: 70px; /* Slightly wider (from 50px) */
          height: 42px; /* Matches pointY */
          display: flex;
          align-items: center;
          justify-content: center;

          :deep(.trigger_value) {
            width: 58px; /* Slightly wider (from 38px) */
            height: 36px; /* Matches keycap feel but not perfectly square now */
            min-height: 36px;
            padding: 0;
            background: #f8f9fa;
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: var(--radius-m);
            box-shadow:
              0 2px 0 rgba(0, 0, 0, 0.05),
              inset 0 1px 0 #fff; /* Keycap style */
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
              background: #fff;
              border-color: var(--brand-color-pure);
              transform: translateY(-2px);
              box-shadow: 0 4px 8px rgba(var(--color-brand-2-rgb), 0.15);
            }

            &.focus {
              background: #fff;
              border-color: var(--brand-color-pure);
              box-shadow: 0 0 0 2px rgba(var(--color-brand-2-rgb), 0.2);
              transform: scale(1.05);
            }

            .slot_label {
              display: none;
            }

            .key_display {
              font-weight: 800;
              color: var(--color-dark);
              width: 100%;
              text-align: center;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .edit_hint {
              display: none;
            }

            &.selected:not(.focus) {
              background: var(--brand-color);
              border-color: transparent;
              box-shadow: 0 2px 0 rgba(0, 0, 0, 0.1);
              .key_display {
                color: #fff;
              }
            }

            &.empty {
              background: rgba(0, 0, 0, 0.02);
              box-shadow: none;
              border-style: dashed;
              .key_display {
                font-size: 10px !important;
                color: var(--color-text-hint);
                opacity: 0.4;
              }
            }

            .trigger_star {
              display: none; /* Hide star for square look */
            }
          }
        }
      }
    }
  }
}
</style>
